import { Forward, Mail, Search, Send, Shield, Zap } from 'lucide-react';
import { Button, Card, Input, Tag, Textarea } from '@/components/ui';
import { ResponseCard } from '@/components/ResponseCard';
import type { ResponseRecord } from '@/lib/types';

// Storybook-style showcase for the Phase 1.4 acceptance test. Renders every
// UI primitive in its variants on dark backgrounds. This page is the source
// of truth for what the building blocks look like; remove or repurpose once
// component documentation moves elsewhere.
export function Design() {
  return (
    <main className="min-h-screen bg-stone-950 px-6 py-10">
      <div className="mx-auto max-w-5xl space-y-10">
        <header className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-500">Design system</p>
          <h1 className="font-display text-3xl italic text-amber-500">Court Copilot primitives</h1>
          <p className="max-w-2xl text-sm text-stone-400">
            Five UI primitives, used everywhere. Buttons, inputs, textareas, cards, tags. The full
            visual language is in 03-ui-design-language.md.
          </p>
        </header>

        <Section label="Buttons">
          <Card variant="panel" padding="lg">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="primary">Primary action</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="primary" disabled>
                Disabled
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button variant="primary" size="sm">
                Small primary
              </Button>
              <Button variant="secondary" size="sm">
                Small secondary
              </Button>
              <Button variant="primary" size="icon" aria-label="Send">
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button variant="listeningIdle" leftIcon={<Mail className="h-4 w-4" />}>
                Start listening
              </Button>
              <Button variant="listeningActive" leftIcon={<Mail className="h-4 w-4" />}>
                Listening
              </Button>
            </div>
          </Card>
        </Section>

        <Section label="Quick action buttons">
          <Card variant="panel" padding="lg">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <QuickAction
                icon={<Zap className="h-4 w-4" />}
                label="Counter argument"
                hotkey="F1"
              />
              <QuickAction
                icon={<Search className="h-4 w-4" />}
                label="Find evidence"
                hotkey="F2"
              />
              <QuickAction
                icon={<Shield className="h-4 w-4" />}
                label="Spot weakness"
                hotkey="F3"
              />
              <QuickAction icon={<Forward className="h-4 w-4" />} label="Next move" hotkey="F4" />
            </div>
          </Card>
        </Section>

        <Section label="Inputs">
          <Card variant="panel" padding="lg" className="space-y-4">
            <div className="space-y-1">
              <Label>Email</Label>
              <Input type="email" placeholder="anthony@cinquegroup.com.au" />
            </div>
            <div className="space-y-1">
              <Label>Password</Label>
              <Input type="password" placeholder="enter password" />
            </div>
            <div className="space-y-1">
              <Label>Invalid input</Label>
              <Input invalid defaultValue="not an email" />
            </div>
            <div className="space-y-1">
              <Label>Custom prompt</Label>
              <Textarea rows={3} placeholder="Type a question for Claude" />
            </div>
            <div className="space-y-1">
              <Label>Live transcript style</Label>
              <Textarea
                monospace
                rows={4}
                defaultValue={'Counsel: Your Honour, the plaintiff seeks an order...\nWitness:'}
              />
            </div>
          </Card>
        </Section>

        <Section label="Cards">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <Card padding="md">
              <Label>Default</Label>
              <p className="mt-2 text-sm text-stone-300">stone-900 background, stone-800 border.</p>
            </Card>
            <Card variant="panel" padding="md">
              <Label>Panel</Label>
              <p className="mt-2 text-sm text-stone-300">stone-925, the new tier behind cards.</p>
            </Card>
            <Card variant="elevated" padding="md">
              <Label>Elevated</Label>
              <p className="mt-2 text-sm text-stone-300">stone-800 with stone-700 border.</p>
            </Card>
            <Card variant="accent" padding="md">
              <Label tone="accent">Accent</Label>
              <p className="mt-2 text-sm text-stone-300">For the live source connections moment.</p>
            </Card>
          </div>
        </Section>

        <Section label="Tags">
          <Card variant="panel" padding="lg">
            <div className="flex flex-wrap items-center gap-2">
              <Tag>Dropbox</Tag>
              <Tag>Microsoft 365</Tag>
              <Tag tone="accent">Active matter</Tag>
              <Tag tone="muted">Cached</Tag>
              <Tag leftIcon={<Search className="h-3 w-3" />}>Searched</Tag>
            </div>
          </Card>
        </Section>

        <Section label="Response cards (Phase 2.6 acceptance fixtures)">
          <div className="space-y-3">
            <ResponseCard state={{ kind: 'loading', label: 'counter-argument' }} />
            <ResponseCard
              state={{
                kind: 'error',
                label: 'find-evidence',
                message:
                  'Anthropic API timed out after 15 seconds. Network may be unstable, or the model is under load.',
                onRetry: () => undefined,
              }}
            />
            <ResponseCard state={{ kind: 'response', response: FIXTURE_WITH_SOURCES }} />
            <ResponseCard state={{ kind: 'response', response: FIXTURE_NO_SOURCES }} />
          </div>
        </Section>
      </div>
    </main>
  );
}

const FIXTURE_WITH_SOURCES: ResponseRecord = {
  id: 'res_fixture_1',
  sessionId: 'ses_fixture',
  prompt: 'What is the strongest counter argument here?',
  label: 'counter-argument',
  answer: [
    'They argue clause 14.2 lets them call the loan on demand.',
    'Clause 14.2 only triggers on a default event listed in clause 13. None of those have occurred.',
    'See loan_agreement_2024.pdf clause 13(a)-(g). Email from CBA dated 14 Mar 2026 confirms no default declared.',
    'Say next: "Your Honour, clause 14.2 only operates after a clause 13 default. No default has been declared."',
  ].join('\n\n'),
  toolsUsed: [
    { server: 'dropbox', tool: 'search_files', input: { query: 'loan agreement clause 13' } },
    { server: 'microsoft365', tool: 'search_messages', input: { query: 'CBA default March 2026' } },
  ],
  durationMs: 8420,
  createdAt: new Date(Date.now() - 90_000).toISOString(),
};

const FIXTURE_NO_SOURCES: ResponseRecord = {
  id: 'res_fixture_2',
  sessionId: 'ses_fixture',
  prompt: 'Is opposing counsel cross-examining outside the scope of evidence in chief?',
  label: 'spot-weakness',
  answer:
    'No. Counsel is staying within the scope of the affidavit at paragraphs 12 to 18. The line of questioning is narrow and proper.',
  toolsUsed: [],
  durationMs: 1240,
  createdAt: new Date(Date.now() - 30_000).toISOString(),
};

interface SectionProps {
  label: string;
  children: React.ReactNode;
}

function Section({ label, children }: SectionProps) {
  return (
    <section className="space-y-3">
      <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</p>
      {children}
    </section>
  );
}

interface LabelProps {
  children: React.ReactNode;
  tone?: 'default' | 'accent';
}

function Label({ children, tone = 'default' }: LabelProps) {
  return (
    <p
      className={
        tone === 'accent'
          ? 'text-[10px] uppercase tracking-[0.2em] text-amber-400'
          : 'text-[10px] uppercase tracking-[0.2em] text-stone-500'
      }
    >
      {children}
    </p>
  );
}

interface QuickActionProps {
  icon: React.ReactNode;
  label: string;
  hotkey: string;
}

function QuickAction({ icon, label, hotkey }: QuickActionProps) {
  return (
    <Button
      variant="quickAction"
      className="justify-start gap-3"
      leftIcon={icon}
      rightSlot={
        <kbd className="rounded bg-stone-800 px-1.5 py-0.5 text-[10px] text-stone-500">
          {hotkey}
        </kbd>
      }
    >
      {label}
    </Button>
  );
}
